import type {
  PostTablesInventoryDevices200,
  PostTablesInventoryDevicesBody,
} from '@ipfabric/api-client/client/schemas';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type {
  GraphDefaultGatewayRequestBody,
  GraphDefaultGatewayResponseBody,
} from '@ipfabric/utils';
import { isPathLookupGraphMetadata } from 'components/Graph/GraphState';
import type { GraphCommandContext } from 'components/Graph/model/GraphCommandContext';
import {
  LIMIT_SN_MAX_COUNT,
  type TechsupportNavigationState,
} from 'pages/Support/Techsupport/Techsupport.utils';

export const goToTechsupportFromPathlookup =
  (cmdContext: GraphCommandContext) =>
  async (): Promise<TechsupportNavigationState | null> => {
    const state = cmdContext.getState();
    if (!isPathLookupGraphMetadata(state.graphMetadata)) {
      return null;
    }

    const { parameters, snapshotId } = state.graphMetadata;
    const pathSns = [
      ...new Set(
        Object.values(state.graph.nodes)
          .map((node) => node.sn)
          .filter((sn): sn is NonNullable<typeof sn> => Boolean(sn)),
      ),
    ];

    let defaultGateways: GraphDefaultGatewayResponseBody;
    try {
      defaultGateways = await cmdContext.api.request<
        GraphDefaultGatewayResponseBody,
        GraphDefaultGatewayRequestBody
      >({
        data: { parameters, snapshot: snapshotId },
        method: 'POST',
        url: '/graphs/default-gateway',
      });
    } catch (error) {
      cmdContext.logError(error, 'GRAPH');
      cmdContext.showNotification({
        message: 'Could not resolve default gateway for techsupport.',
        type: 'danger',
      });
      return null;
    }

    const candidateSns = [
      ...new Set<string>([
        ...pathSns,
        ...defaultGateways.source,
        ...defaultGateways.target,
      ]),
    ];

    if (candidateSns.length === 0) {
      stumble('Devices not selected');
      return { limitSns: [] };
    }

    let inventoryDevices: PostTablesInventoryDevices200;
    try {
      // Pathlookup graphs may include synthetic hosts/clouds, so let inventory
      // devices decide which collected SNs are valid for techsupport.
      inventoryDevices = await cmdContext.api.request<
        PostTablesInventoryDevices200,
        PostTablesInventoryDevicesBody
      >({
        data: {
          columns: ['sn'],
          filters: {
            or: candidateSns.map((sn) => ({ sn: ['eq', sn] })),
          },
          snapshot: snapshotId,
        },
        method: 'POST',
        url: '/tables/inventory/devices',
      });
    } catch (error) {
      cmdContext.logError(error, 'GRAPH');
      cmdContext.showNotification({
        message: 'Could not resolve devices for techsupport.',
        type: 'danger',
      });
      return null;
    }

    const limitSns = [
      ...new Set(
        inventoryDevices.data
          .map(({ sn }) => sn)
          .filter((sn): sn is string => typeof sn === 'string'),
      ),
    ];
    if (limitSns.length === 0) {
      cmdContext.showNotification({
        message: 'Could not resolve devices for techsupport.',
        type: 'warning',
      });
      return null;
    }

    if (limitSns.length > LIMIT_SN_MAX_COUNT) {
      cmdContext.showNotification({
        message: `Too many devices to send to techsupport (${limitSns.length}); the limit is ${LIMIT_SN_MAX_COUNT}.`,
        type: 'warning',
      });
      return null;
    }

    return { limitSns };
  };
